/**
 * Startup recovery helpers — used by the bootstrap path in main.tsx
 * to recover from corrupted local browser state without depending on
 * React, the router, or any UI library.
 */

export async function clearAllLocalAppData(): Promise<void> {
  // 1. Clear React Query persisted cache (IndexedDB via idb-keyval).
  try {
    const { clearPersistedCache } = await import("@/lib/idb-persister");
    await clearPersistedCache();
  } catch { /* ignore */ }

  // 2. Best-effort: drop all IndexedDB databases.
  try {
    const anyIdb = indexedDB as unknown as { databases?: () => Promise<{ name?: string }[]> };
    if (typeof anyIdb.databases === "function") {
      const dbs = await anyIdb.databases();
      await Promise.all(
        (dbs || []).map((d) =>
          d?.name
            ? new Promise<void>((resolve) => {
                const req = indexedDB.deleteDatabase(d.name as string);
                req.onsuccess = req.onerror = req.onblocked = () => resolve();
              })
            : Promise.resolve()
        )
      );
    }
  } catch { /* ignore */ }

  // 3. Clear web storage.
  try { localStorage.clear(); } catch { /* ignore */ }
  try { sessionStorage.clear(); } catch { /* ignore */ }

  // 4. Clear caches API and unregister service workers.
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* ignore */ }
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* ignore */ }
}

/**
 * Render a minimal, dependency-free recovery UI directly into a container.
 * Does not import React, lucide, or any app providers.
 */
export function renderBootFallback(container: HTMLElement, message: string): void {
  container.innerHTML = "";
  const root = document.createElement("div");
  root.style.cssText =
    "min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;background:#0b0b0c;color:#f4f4f5;";

  const card = document.createElement("div");
  card.style.cssText =
    "max-width:420px;width:100%;text-align:center;background:#141416;border:1px solid #2a2a2e;border-radius:12px;padding:24px;";

  const icon = document.createElement("div");
  icon.textContent = "⚠️";
  icon.style.cssText = "font-size:32px;margin-bottom:12px;";

  const title = document.createElement("h1");
  title.textContent = "App failed to start";
  title.style.cssText = "font-size:18px;font-weight:600;margin:0 0 8px 0;";

  const desc = document.createElement("p");
  desc.textContent = message || "Something went wrong while loading the app.";
  desc.style.cssText = "font-size:14px;line-height:1.5;color:#a1a1aa;margin:0 0 20px 0;";

  const row = document.createElement("div");
  row.style.cssText = "display:flex;gap:8px;justify-content:center;flex-wrap:wrap;";

  const reloadBtn = document.createElement("button");
  reloadBtn.textContent = "Reload";
  reloadBtn.style.cssText =
    "padding:10px 16px;border-radius:8px;border:1px solid #3f3f46;background:#27272a;color:#fafafa;font-size:14px;cursor:pointer;";
  reloadBtn.onclick = () => { try { location.reload(); } catch { /* ignore */ } };

  const resetBtn = document.createElement("button");
  resetBtn.textContent = "Reset local data & reload";
  resetBtn.style.cssText =
    "padding:10px 16px;border-radius:8px;border:1px solid #3f3f46;background:#dc2626;color:#fff;font-size:14px;cursor:pointer;";
  resetBtn.onclick = async () => {
    resetBtn.disabled = true;
    resetBtn.textContent = "Resetting…";
    try { await clearAllLocalAppData(); } catch { /* ignore */ }
    try { location.reload(); } catch { /* ignore */ }
  };

  row.appendChild(reloadBtn);
  row.appendChild(resetBtn);
  card.appendChild(icon);
  card.appendChild(title);
  card.appendChild(desc);
  card.appendChild(row);
  root.appendChild(card);
  container.appendChild(root);
}

export function isPreviewHost(hostname: string): boolean {
  // Only true preview hosts — NOT the published *.lovable.app domain.
  if (hostname.includes("lovableproject.com")) return true;
  if (hostname.startsWith("id-preview--")) return true;
  return false;
}

/* ------------------------------------------------------------------ */
/* Deployment-mismatch + chunk-load recovery                          */
/* ------------------------------------------------------------------ */

const DEPLOY_KEY = "whisprai_deploy_id_v1";
const RECOVERY_FLAG = "whisprai_chunk_recovery_v1";

function safeGetSession(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeSetSession(key: string, val: string): void {
  try { sessionStorage.setItem(key, val); } catch { /* ignore */ }
}
function safeGetLocal(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSetLocal(key: string, val: string): void {
  try { localStorage.setItem(key, val); } catch { /* ignore */ }
}

/**
 * One-shot full recovery: unregister SWs, drop caches/IDB/localStorage,
 * then reload exactly once per browser session. If we've already tried
 * once this session, do nothing so we don't loop.
 */
export async function attemptOneShotRecovery(reason: string): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (safeGetSession(RECOVERY_FLAG)) {
    try { console.warn("[recovery] already attempted this session, skipping", reason); } catch { /* ignore */ }
    return false;
  }
  safeSetSession(RECOVERY_FLAG, "1");
  try { console.warn("[recovery] one-shot recovery triggered:", reason); } catch { /* ignore */ }
  try { await clearAllLocalAppData(); } catch { /* ignore */ }
  try { window.location.reload(); } catch { /* ignore */ }
  return true;
}

/**
 * Compare the live document's deployment id (Lovable hosting sets
 * `x-deployment-id`) against the one we last booted with. If it's
 * changed, do a one-shot reset+reload so the user picks up the new
 * HTML/chunks cleanly instead of crashing on stale references.
 *
 * No-ops on preview hosts (they already wipe caches every load).
 */
export async function checkDeploymentAndRecover(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (isPreviewHost(window.location.hostname)) return false;

  try {
    const res = await fetch(window.location.pathname, {
      method: "HEAD",
      cache: "no-store",
      credentials: "same-origin",
    });
    const current = res.headers.get("x-deployment-id") || res.headers.get("x-vercel-id");
    if (!current) return false;

    const stored = safeGetLocal(DEPLOY_KEY);
    if (!stored) {
      safeSetLocal(DEPLOY_KEY, current);
      return false;
    }
    if (stored !== current) {
      safeSetLocal(DEPLOY_KEY, current);
      const ok = await attemptOneShotRecovery(`deployment changed ${stored} -> ${current}`);
      return ok;
    }
  } catch { /* network blip — ignore */ }
  return false;
}

function isChunkLoadMessage(msg: string | undefined | null): boolean {
  if (!msg) return false;
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk \S+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

/**
 * Listen for the dynamic-import / chunk-load failure family and trigger
 * one-shot recovery. Always-on: covers both preview and production.
 */
export function installChunkLoadRecovery(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event: ErrorEvent) => {
    const msg = event?.message || (event?.error && (event.error as Error)?.message);
    if (isChunkLoadMessage(msg)) {
      void attemptOneShotRecovery(`window.error: ${msg}`);
    }
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason: any = event?.reason;
    const msg = reason?.message || (typeof reason === "string" ? reason : "");
    if (isChunkLoadMessage(msg)) {
      void attemptOneShotRecovery(`unhandledrejection: ${msg}`);
    }
  });

  // CAPTURE-phase listener: failed <script>/<link>/<img> loads fire an
  // `error` event on the element itself and do NOT bubble. This catches
  // the stale-deploy case where the cached index.html references asset
  // hashes that no longer exist (404). When we see one of OUR /assets/
  // URLs fail, trigger one-shot recovery instead of waiting for the
  // 12s boot watchdog.
  window.addEventListener(
    "error",
    (event: Event) => {
      const target = event?.target as
        | (HTMLElement & { src?: string; href?: string; tagName?: string })
        | null;
      if (!target || !target.tagName) return;
      const tag = target.tagName.toUpperCase();
      if (tag !== "SCRIPT" && tag !== "LINK") return;
      const url = target.src || target.href || "";
      if (!url) return;
      // Only react to our own bundled assets — never to third-party scripts.
      let pathname = "";
      try { pathname = new URL(url, window.location.origin).pathname; } catch { return; }
      if (!pathname.startsWith("/assets/")) return;
      void attemptOneShotRecovery(`asset load failed: ${pathname}`);
    },
    true,
  );
}